import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TranscriptTurn from "components/CallTranscript/TranscriptTurn";
import callConstants from "constants/Call";

const { SPEAKER_ROLES } = callConstants;

/** Render a language code as its name, e.g. "es-US" becomes "Spanish". */
const languageName = code => {
  if (!code) return null;
  try {
    const display = new Intl.DisplayNames([navigator.language], {
      type: "language",
    });
    return display.of(code.split("-")[0]) ?? code;
  } catch {
    return code;
  }
};

function TurnList({ turns, speakers }) {
  return (
    <ul className="tw:flex tw:flex-col tw:gap-1">
      {turns.map((turn, position) => {
        const speaker = speakers?.[turn.speaker_id];
        return (
          <TranscriptTurn
            key={turn.index ?? position}
            turn={turn}
            label={speaker?.label ?? turn.speaker_id}
            isShaded={speaker?.role !== SPEAKER_ROLES.REP}
          />
        );
      })}
    </ul>
  );
}

/**
 * Read-only turn-by-turn transcript. Calls in another language get an English
 * tab first, since the translation is what most readers want, with the original
 * kept alongside it for reference.
 *
 * Speakers are attributed automatically and cannot always be right, so a call
 * with both roles resolved offers a swap.
 */
export function CallTranscriptView({
  turns,
  turnsEn,
  speakers,
  translatedFrom,
  translationFailed,
  canSwapSpeakers,
  speakersSwapped,
  onSwapSpeakers,
  onDownload,
  isDownloading,
}) {
  const controls = (
    <div className="tw:flex tw:items-center tw:gap-2">
      {canSwapSpeakers && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onSwapSpeakers(!speakersSwapped)}
        >
          Swap Rep/Contact
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={isDownloading}
        onClick={onDownload}
      >
        {isDownloading && <Spinner />}
        Download
      </Button>
    </div>
  );

  if (!turnsEn?.length) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-2">
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          {translationFailed ? (
            <p className="tw:text-xs tw:text-[var(--color-text-muted)]">
              English translation unavailable
            </p>
          ) : (
            <span />
          )}
          {controls}
        </div>
        <TurnList turns={turns} speakers={speakers} />
      </div>
    );
  }

  return (
    <Tabs defaultValue="english">
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <TabsList>
          <TabsTrigger value="english">English</TabsTrigger>
          <TabsTrigger value="original">Original</TabsTrigger>
        </TabsList>
        {controls}
      </div>
      <TabsContent value="english">
        <p className="tw:mb-2 tw:text-xs tw:text-[var(--color-text-muted)]">
          Translated from {languageName(translatedFrom)}
        </p>
        <TurnList turns={turnsEn} speakers={speakers} />
      </TabsContent>
      <TabsContent value="original">
        <TurnList turns={turns} speakers={speakers} />
      </TabsContent>
    </Tabs>
  );
}

export default CallTranscriptView;
